import { Component, OnInit, ElementRef, ViewChild, forwardRef, Input, SimpleChanges, OnChanges, HostListener, HostBinding, Output, EventEmitter, Inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNil, set } from 'lodash-es';
import { Styles } from './models/style';
import { SelectorComponent } from './components/selector.component';
import { ESelectorType } from './components/selector.component';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { Carriers_ShellService } from './Carriers.shell.service';
import { Carriers_OperationService } from './Carriers.operation.service';
import { Carriers_DatasourceService } from './Carriers.datasource.index';
import { Carriers_FlowService } from './Carriers.flow.index';
import { Carriers_ReportService } from './Carriers.report.index';
import { Carriers_LocalizationService } from './Carriers.localization.service';
import { Carriers_carriers_ownerscarriers_dd_ComponentContextService } from './Carriers.carriers_ownerscarriers_dd.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './Carriers.frontend.types'
import { $frontendTypes as $types} from './Carriers.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'Carriers-carriers_ownerscarriers_dd_multi',
  templateUrl: './Carriers.carriers_ownerscarriers_dd_multi.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Carriers_carriers_ownerscarriers_dd_multiComponent),
      multi: true
    }
  ]
})
export class Carriers_carriers_ownerscarriers_dd_multiComponent extends BaseComponent implements OnInit, ControlValueAccessor, OnChanges {

  @Input() type?: ESelectorType;
  _disabled = false;
  @Input() get disabled() { 
      return this._disabled 
      || this.$hasMissingRequiredInParams
      ; }
  set disabled(val: any) { this._disabled = val; }

  @Input() placeholder = '';
  @Input() styles: Styles;
  @Input() tooltip = '';

  @Output() displayTextChange: EventEmitter<string> = new EventEmitter();

  @ViewChild(SelectorComponent) selector: SelectorComponent;

  @HostBinding('tabIndex') get tabIndex() { return -1; }
  @HostListener('focus')
  focus() {
    this.selector?.focus();
  }

  get isMulti() {
    return true;
  }

  get isSingle() {
    return false;
  }


  inParams: { ownerId: number } = { ownerId: null };
  @Input('ownerId') set $inParams_ownerId(value: any) {
    this.inParams['ownerId'] = value;
  }
  get $inParams_ownerId(): any {
    return this.inParams['ownerId'] ;
  }

  fullTextSearch: string;

  constructor(
    private $utils: UtilsService,
    private $settings: SettingsValuesService,
    private $shell: Carriers_ShellService,
    private $datasources: Carriers_DatasourceService,
    private $flows: Carriers_FlowService,
    private $reports: Carriers_ReportService,
    private $localization: Carriers_LocalizationService,
    private $operations: Carriers_OperationService,
    private $logger: CleanupLoggerService,
    private $context: Carriers_carriers_ownerscarriers_dd_ComponentContextService,
    private _elementRef: ElementRef
    ) {
      super();
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if (!this.$hasMissingRequiredInParams) {
      this.$init();
    } else {
      this.$initEmpty();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      } else {
        this.$initEmpty();
      }
    }
  }

  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.ownerId)) {
        this.$missingRequiredInParams.push('ownerId');
      }
  }

  async $init() {
    this.selector?.init();
  }

  onKeyDown($event: KeyboardEvent) {
    //TODO:KeyDown    
  }

  //ControlValueAccessor
  _innerValue: any;

  onChange: any = () => { };
  onTouch: any = () => { };

  get value(): number | number[] {
    return this._innerValue;
  }

  set value(val:  number | number[]) {
    if (this._innerValue !== val) {
      this._innerValue = val;
      this.onChange(val);
      this.onTouch(val);
    }
  }

  writeValue(value: any): void {
    this._innerValue = value;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  displayWithFn(value: any): Promise<{displayText:string, selected :Array<{ key: any; name: string; disabled?: boolean; selected?: boolean;}>}> {
    const promise = new Promise<{displayText:string, selected :Array<{ key: any; name: string; disabled?: boolean; selected?: boolean;}>}>((resolve, reject) => {

      const $selector = this;
      const $utils = this.$utils;

      this.fullTextSearch = null;
      const $fullTextSearch = null;
      this.$datasources.Carriers.ds_carriers_ownerscarriers_dd.getByKeys({
        $keys: value,
        ownerId:  $selector.inParams.ownerId ,
        fullTextSearch:  $selector.fullTextSearch ,
      }).then(result => {
        const selected = value.map(k => {
          const found = result.result
            .find(i => i.Id === k);
          const e: { Id?: number, Name?: string } = found 
            ?? { 
              Id : k
            };

          const $option = { entity: e };
          const $entity = e;
          const $resultOption = { key: null, name: found ? $option.entity.Name : 'No data to display', disabled: false, selected:true };
          $resultOption.key = $option.entity.Id;
          return $resultOption;
        });
        const displayText = value.map(k => {
          const found = result.result
            .find(i => i.Id === k);
          if (found) {
            const $option = { entity: found };
            const $entity = found;
            return $option.entity.Name;
          } else {
            return 'No data to display';
          }
        })
        .join(
           ', '
        );
        this.displayTextChange.emit(displayText);
        resolve({displayText, selected});
      });
    });
    return promise;
  }

  optionsFn(filterText: string): Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }> {

    const promise = new Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }>((resolve, reject) => {

      const $selector = this;
      const $utils = this.$utils;

      this.fullTextSearch = filterText;
      const $fullTextSearch = filterText;
      this.$datasources.Carriers.ds_carriers_ownerscarriers_dd.getList({
        $top: 15,
        $skip: 0,
        ownerId:  $selector.inParams.ownerId ,
        fullTextSearch:  $selector.fullTextSearch ,
      }).then(result => {
        resolve({
          list: result.result.map(item => {
            const $option = { entity: item };
            const $entity = item;
            const $resultOption = { key: null, name: $option.entity.Name, disabled: null };
            $resultOption.key = $option.entity.Id;
            return $resultOption;
          }),
          totalCount: result.totalCount,
          top: 15
        });
      });

    });

    return promise;

  }

  //#region private flows
  on_add_action(event = null) {
    return this.on_add_actionInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_add_actionInternal(
    $selector: Carriers_carriers_ownerscarriers_dd_multiComponent,
  
    $shell: Carriers_ShellService,
    $datasources: Carriers_DatasourceService,
    $flows: Carriers_FlowService,
    $reports: Carriers_ReportService,
    $settings: SettingsValuesService,
    $operations: Carriers_OperationService,
    $utils: UtilsService,
    $context: Carriers_carriers_ownerscarriers_dd_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: Carriers_LocalizationService,
    $event: any
  ) {
  
  
  const newCarrier = $selector.fullTextSearch;
  
  if ($utils.isDefined(newCarrier)) {
      // Check for carrier duplicates
      const carriers = (await $datasources.Carriers.ds_get_carrier_by_name.get({ name: newCarrier })).result;
      if ($utils.isDefined(carriers)) {
  
          const carrierId = carriers[0].Id;
          const payloadCarrierOwner = {
  
              "CarrierId": carrierId,
              "OwnerId": $selector.inParams.ownerId
  
          };
          try {
              await $flows.Utilities.crud_create_flow({ entitySet: 'OwnersCarriersLookup', entity: payloadCarrierOwner });
              // Set the dropdown value
  
              $selector.value = carrierId;
          }
          catch (error) {
  
              $shell.Carriers.showErrorDetails('Save', 'Error on save.', error);
              throw error; // to prevent displayMode 
          }
      }
      else {
  
          // Create carrier
  
          const payload = {
  
              "Name": newCarrier,
              "ShortName": newCarrier
  
          }
  
  
          try {
  
              await $flows.Utilities.crud_create_flow({ entitySet: 'Carriers', entity: payload });
  
          }
          catch (error) {
  
              $shell.Carriers.showErrorDetails('Save', 'Error on save.', error);
              throw error; // to prevent displayMode 
          }
  
          // Get new carrier and add it to the owner
          const carrierOwner = (await $datasources.Carriers.ds_get_carrier_by_name.get({ name: newCarrier })).result;
          if ($utils.isDefined(carrierOwner)) {
  
              const carrierId = carrierOwner[0].Id;
  
              const payloadCarrierOwner = {
  
                  "CarrierId": carrierId,
                  "OwnerId": $selector.inParams.ownerId
  
              };
              try {
                  await $flows.Utilities.crud_create_flow({ entitySet: 'OwnersCarriersLookup', entity: payloadCarrierOwner });
                  // Set the dropdown value
  
                  $selector.value = carrierId;
              }
              catch (error) {
  
                  $shell.Carriers.showErrorDetails('Save', 'Error on save.', error);
                  throw error; // to prevent displayMode 
              }
  
  
  
          }
  
      }
  
  
  }
  
  else {
      throw new Error ('To create a new carrier please first type in the desired carrier in the drop down then try again.')
  }
  }
  //#endregion private flows
}
